import React, { useState, useEffect, useRef } from "react";
import { usePsChat } from "../../../context/PsChatMessageContext";

const BlockCallsAlert = ({
  isOpen,
  onClose,
  threadId,
  userId,
  currentlyBlocked = false,
}) => {
  const { blockCalls } = usePsChat();
  const [reason, setReason] = useState("Persistent calling");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose?.();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (blockValue) => {
    if (!threadId || !userId) {
      console.warn("[BlockCallsAlert] Missing threadId or userId");
      onClose?.();
      return;
    }

    setSaving(true);
    try {
      const finalReason = note
        ? `${reason} — ${note}`
        : reason;

      await blockCalls(threadId, blockValue, finalReason, userId);
      onClose?.();
    } catch (err) {
      console.error("[BlockCallsAlert] Failed to update call block:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleBlock = () => {
    handleSubmit(true);
  };

  const handleUnblock = () => {
    handleSubmit(false);
  };

  const handleCancel = () => {
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="alert-overlay" />

      <div
        className="alert-window"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{currentlyBlocked ? "Unblock Calls" : "Block Calls"}</h2>
        <p>
          {currentlyBlocked
            ? "Calls from this user are now enabled."
            : "You won’t receive calls from this user."}
        </p>

        <label htmlFor="reason">Reason:</label>
        <select
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={saving}
        >
          <option>Persistent calling</option>
          <option>Persistent messaging</option>
          <option>Harassment or abuse</option>
          <option>Inappropriate content</option>
          <option>Other</option>
        </select>

        <label htmlFor="note">Leave a Note (optional):</label>
        <textarea
          id="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add context for this action (visible to admins only)..."
          disabled={saving}
        />

        <div className="alert-actions">
          <button onClick={handleCancel} disabled={saving}>
            Cancel
          </button>

          {currentlyBlocked && (
            <button
              onClick={handleUnblock}
              disabled={saving}
              className="btn-secondary"
            >
              {saving ? "Updating..." : "Unmute Calls"}
            </button>
          )}

          {!currentlyBlocked && (
            <button
              onClick={handleBlock}
              disabled={saving}
              className="btn-danger"
            >
              {saving ? "Blocking..." : "Block Calls"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default BlockCallsAlert;
